'use client';
import { useEffect, useRef } from 'react';

// Dipende da GSAP già registrato dal progetto (src/utils/gsap.js).
import { gsap } from '@/utils/gsap';

/**
 * Cursore custom (punto + anello che segue con inerzia). Si disattiva da solo
 * su touch/coarse pointer (mobile/tablet) — nessuna prop richiesta.
 * Si ingrandisce sopra elementi interattivi (a, button, [data-cursor-hover]).
 * @param {{ hoverSelector?: string }} props
 */
export function CustomCursor({ hoverSelector = 'a, button, [data-cursor-hover]' }) {
  const dotRef = useRef(null);
  const ringRef = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!window.matchMedia('(pointer: fine)').matches) return; // solo desktop/mouse

    const dot = dotRef.current;
    const ring = ringRef.current;
    if (!dot || !ring) return;

    const moveDot = gsap.quickTo(dot, 'x', { duration: 0.1, ease: 'power3.out' });
    const moveDotY = gsap.quickTo(dot, 'y', { duration: 0.1, ease: 'power3.out' });
    const moveRing = gsap.quickTo(ring, 'x', { duration: 0.35, ease: 'power3.out' });
    const moveRingY = gsap.quickTo(ring, 'y', { duration: 0.35, ease: 'power3.out' });

    function onMove(e) {
      moveDot(e.clientX);
      moveDotY(e.clientY);
      moveRing(e.clientX);
      moveRingY(e.clientY);
    }
    function onEnter() { gsap.to(ring, { scale: 1.8, duration: 0.25 }); }
    function onLeave() { gsap.to(ring, { scale: 1, duration: 0.25 }); }

    window.addEventListener('mousemove', onMove);
    const targets = document.querySelectorAll(hoverSelector);
    targets.forEach((t) => { t.addEventListener('mouseenter', onEnter); t.addEventListener('mouseleave', onLeave); });
    document.body.style.cursor = 'none';

    return () => {
      window.removeEventListener('mousemove', onMove);
      targets.forEach((t) => { t.removeEventListener('mouseenter', onEnter); t.removeEventListener('mouseleave', onLeave); });
      document.body.style.cursor = '';
    };
  }, [hoverSelector]);

  if (typeof window !== 'undefined' && !window.matchMedia('(pointer: fine)').matches) return null;

  return (
    <>
      <div
        ref={dotRef}
        className="fixed top-0 left-0 w-1.5 h-1.5 rounded-full bg-theme-accent pointer-events-none z-[9998]"
        style={{ transform: 'translate(-50%,-50%)' }}
      />
      <div
        ref={ringRef}
        className="fixed top-0 left-0 w-8 h-8 rounded-full border border-theme-accent pointer-events-none z-[9998]"
        style={{ transform: 'translate(-50%,-50%)' }}
      />
    </>
  );
}
